/**
 * Vehicle values edited on a work sheet → the body of the work-order PATCH.
 *
 * A work order has no vehicle fields: the make, model, year, colour and technical data belong to the linked
 * vehicle. The Info tab nevertheless lets the technician edit them, and they used to stay on the phone — the PATCH
 * never carried them, so a typed year never reached the vehicle (and the next reload showed it blank). The backend
 * now accepts these as flat keys on `PATCH /serviceModule/work-orders/{id}` and writes them to the linked vehicle
 * (linking or registering one when the work order has none).
 *
 * Pure on purpose (no React Native imports) so it can be unit-tested; `WorkSheetContext` merges it into the PATCH.
 *
 * @module helpers/workSheetVehicleEdit
 */

/**
 * `[work-sheet field, flat PATCH key]` for every vehicle value the Info tab edits.
 * @type {Array<[string, string]>}
 */
export const VEHICLE_EDIT_FIELDS = [
    ['year', 'vehicleYear'],
    ['fuel', 'fuel'],
    ['color', 'color'],
    ['engineCode', 'engineCode'],
    ['displacement', 'displacement'],
    ['powerKw', 'powerKw'],
    ['powerHp', 'powerHp'],
    ['firstRegistration', 'firstRegistration'],
    ['motExpiry', 'motExpiry'],
];

const text = (value) => (value === null || value === undefined ? '' : String(value).trim());

/**
 * Build the vehicle part of a work-sheet PATCH.
 *
 * Only a value the vehicle (or the work order) does not already hold is sent, and never an empty one: a full-state
 * save must not rewrite — let alone clear — a vehicle field because the cached work sheet was blank or stale, and
 * a value merely DERIVED for display (the year from the first-registration date) must not be written back as if
 * the technician had typed it.
 *
 * @param {object} [ws] The (normalised) work sheet being saved (`year`, `fuel`, … plus `vehicleData`, `attributes`).
 * @returns {object} Flat vehicle keys to merge into the PATCH body (possibly empty).
 */
export const buildVehicleEditPayload = (ws = {}) => {
    const vehicle = ws.vehicleData && typeof ws.vehicleData === 'object' ? ws.vehicleData : {};
    const vehicleAttrs = vehicle.attributes || {};
    const workOrderAttrs = ws.attributes || {};
    const out = {};

    VEHICLE_EDIT_FIELDS.forEach(([field, key]) => {
        const next = text(ws[field]);

        if (!next) {
            return;
        }

        const held = [
            vehicleAttrs[field],
            vehicle[field],
            workOrderAttrs[field],
            field === 'year' ? text(vehicleAttrs.firstRegistration).slice(0, 4) : '',
        ].map(text);

        if (!held.includes(next)) {
            out[key] = next;
        }
    });

    return out;
};

export default buildVehicleEditPayload;
